"use client"

import { forwardRef } from "react"
import clsx from "clsx"
import { useInView } from "react-intersection-observer"

import type { PropsWithClassName } from "~/@types/generics"
import { AlgoliaFilterButton } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/AlgoliaCollectionToolbar/AlgoliaFilterButton"
import { AlgoliaGridLayoutSwitcher } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/AlgoliaCollectionToolbar/AlgoliaGridLayoutSwitcher"
import { AlgoliaSortBy } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/AlgoliaCollectionToolbar/AlgoliaSortBy/index.client"
import { CollectionNavigation } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/AlgoliaCollectionToolbar/CollectionNavigation"
import type { AlgoliaListingGridProps } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/index.client"
import ArrowButton from "~/components/ui/SliderNavigation/components/ArrowButton"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

type AlgoliaCollectionToolbarProps = Pick<AlgoliaListingGridProps, "collectionNavigationLinks" | "isEmpty"> & {
  isGridInView?: boolean
  isFixed?: boolean
}

const AlgoliaCollectionToolbarContainer = forwardRef<HTMLDivElement, PropsWithClassName<AlgoliaCollectionToolbarProps>>(
  ({ className, collectionNavigationLinks, isFixed, isEmpty }, ref?) => {
    const t = useTranslate()
    const hasNavigation = collectionNavigationLinks && collectionNavigationLinks?.length > 0

    if (isEmpty && !hasNavigation) return null

    return (
      <div className={clsx(className, css.toolbarContainer, { isFixed, hasNavigation })} ref={ref}>
        {!isFixed && (
          <CollectionNavigation className={css.navigation} collectionNavigationLinks={collectionNavigationLinks} />
        )}
        <div className={clsx(css.buttons)}>
          {isFixed && (
            <ArrowButton
              onClick={() => {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                })
              }}
              theme="black"
              direction="top"
              className={css.backToTop}
              ariaLabel={t("aria_back_to_top")}
            />
          )}

          {!isEmpty && (
            <>
              <AlgoliaGridLayoutSwitcher className={css.gridLayoutSwitcher} />
              <AlgoliaSortBy />
              <AlgoliaFilterButton />
            </>
          )}
        </div>
      </div>
    )
  }
)

export function AlgoliaCollectionToolbar({
  collectionNavigationLinks,
  isGridInView = false,
  isEmpty,
}: AlgoliaCollectionToolbarProps) {
  const [toolbarRef, toolbarInView] = useInView({
    initialInView: true,
    threshold: 0.2,
    rootMargin: "-10% 0px 100% 0px",
  })

  return (
    <>
      <AlgoliaCollectionToolbarContainer
        className={clsx(css.toolbar, sprinkles({ backgroundColor: "grey-alabaster" }))}
        ref={toolbarRef}
        collectionNavigationLinks={collectionNavigationLinks}
        isEmpty={isEmpty}
      />
      <AlgoliaCollectionToolbarContainer
        className={clsx(css.toolbarFixed({ active: !toolbarInView && isGridInView }))}
        isFixed
        collectionNavigationLinks={collectionNavigationLinks}
        isEmpty={isEmpty}
      />
    </>
  )
}
